import { useField } from "formik";

import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
  useComboboxAnchor,
} from "@/components/ui/combobox";
import { Label } from "@/components/ui/label";

type OptionValue = string | number;
type OptionObject = {
  displayName?: string;
  value?: OptionValue;
  [key: string]: string | number | boolean | null | undefined;
};
type Option = string | OptionObject;

interface MultiSelectFormControlProps<TOption extends Option> {
  id: string;
  label: string;
  options: TOption[];
  value: OptionValue[];
  handleSubmit: () => void;
  getOptionLabel?: (option: TOption) => string;
  getOptionValue?: (option: TOption) => OptionValue;
  displayField?: string;
}

export const MultiSelectFormControl = <TOption extends Option>({
  id,
  label,
  options,
  value,
  handleSubmit,
  getOptionLabel,
  getOptionValue,
  displayField = "displayName",
}: MultiSelectFormControlProps<TOption>) => {
  const [, , helpers] = useField(id);
  const anchorRef = useComboboxAnchor();
  const isObjectOptions = options.length > 0 && typeof options[0] === "object";

  const defaultGetOptionLabel = (option: TOption): string => {
    if (getOptionLabel) return getOptionLabel(option);
    if (typeof option === "string") return option;
    const fromDisplayField = option[displayField];
    if (typeof fromDisplayField === "string") return fromDisplayField;
    if (typeof option.displayName === "string") return option.displayName;
    return String(option);
  };

  const defaultGetOptionValue = (option: TOption): OptionValue => {
    if (typeof option === "string") return option;
    if (getOptionValue) return getOptionValue(option);
    if (option.value !== undefined) return option.value;
    return String(option);
  };

  const selectedOptions: TOption[] = isObjectOptions
    ? options.filter((opt) => value.includes(defaultGetOptionValue(opt)))
    : (value as TOption[]);

  const handleValueChange = (newValues: TOption[]) => {
    const valuesToStore = isObjectOptions
      ? newValues.map((item) => defaultGetOptionValue(item))
      : newValues;
    helpers.setValue(valuesToStore || []);
    handleSubmit();
  };

  return (
    <div ref={anchorRef} className="flex flex-col gap-1.5 w-full">
      <Label htmlFor={id}>{label}</Label>
      <Combobox
        multiple
        value={selectedOptions}
        onValueChange={handleValueChange}
        itemToStringLabel={defaultGetOptionLabel}
        itemToStringValue={(item: TOption) =>
          String(defaultGetOptionValue(item))
        }
        isItemEqualToValue={(a: TOption, b: TOption) =>
          defaultGetOptionValue(a) === defaultGetOptionValue(b)
        }
      >
        <ComboboxChips className="flex-nowrap overflow-hidden min-w-0">
          {selectedOptions.length <= 1 ? (
            selectedOptions.map((option) => (
              <ComboboxChip key={defaultGetOptionValue(option)}>
                <span className="max-w-[120px] truncate text-xs">
                  {defaultGetOptionLabel(option)}
                </span>
              </ComboboxChip>
            ))
          ) : (
            <ComboboxChip key="summary">
              <span className="text-xs whitespace-nowrap">
                {selectedOptions.length} selected
              </span>
            </ComboboxChip>
          )}
          <ComboboxChipsInput
            id={id}
            placeholder={selectedOptions.length === 0 ? label : ""}
            className="min-w-0 w-0 flex-shrink"
          />
        </ComboboxChips>
        <ComboboxContent anchor={anchorRef}>
          <ComboboxList>
            {options.length == 0 && (
              <ComboboxEmpty>No results found.</ComboboxEmpty>
            )}
            {options.map((option) => (
              <ComboboxItem key={defaultGetOptionValue(option)} value={option}>
                {defaultGetOptionLabel(option)}
              </ComboboxItem>
            ))}
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
    </div>
  );
};
